import { useEffect, useState } from 'react';

// Loads the Square Web Payments SDK and hands back an initialized
// `payments` object.
//
// The script must come from Square's CDN — it cannot be bundled, and the
// sandbox and production builds live at different hosts. Which one to use
// arrives with the menu payload, so nothing here needs build-time config.

// ── Minimal typings ───────────────────────────────────────────────────
// Only what this app actually calls. The real SDK surface is much larger,
// and `any` is not an option in this codebase.

export interface SquareTokenResult {
    status: 'OK' | 'Cancel' | 'Abort' | string;
    token?: string;
    errors?: { message?: string }[];
}

export interface SquareCard {
    attach(selector: string): Promise<void>;
    tokenize(): Promise<SquareTokenResult>;
    destroy(): Promise<void>;
}

/** The card field renders inside Square's own iframe, so page CSS cannot
 *  reach it. Appearance is passed in at construction instead: keys are the
 *  selectors Square exposes, values are camelCased CSS properties. */
export type SquareCardStyle = Record<string, Record<string, string>>;

export interface SquareCardOptions {
    style?: SquareCardStyle;
}

export interface SquareWalletButton {
    attach(selector: string): Promise<void>;
    tokenize(): Promise<SquareTokenResult>;
    destroy(): Promise<void>;
}

export interface SquarePaymentRequest {
    countryCode: string;
    currencyCode: string;
    total: { amount: string; label: string };
}

export interface SquareVerificationDetails {
    amount: string;
    currencyCode: string;
    intent: 'CHARGE';
    billingContact: Record<string, string>;
    customerInitiated: boolean;
    sellerKeyedIn: boolean;
}

export interface SquarePayments {
    card(options?: SquareCardOptions): Promise<SquareCard>;
    paymentRequest(request: SquarePaymentRequest): unknown;
    applePay(request: unknown): Promise<SquareWalletButton>;
    googlePay(request: unknown): Promise<SquareWalletButton>;
    verifyBuyer(
        source: string,
        details: SquareVerificationDetails
    ): Promise<{ token?: string } | null>;
}

interface SquareGlobal {
    payments(applicationId: string, locationId: string): SquarePayments;
}

declare global {
    interface Window {
        Square?: SquareGlobal;
    }
}

// ── Script loading ────────────────────────────────────────────────────
// One promise per URL for the whole page life: React 18 mounts effects
// twice in development, and two <script> tags would race.

const loaders = new Map<string, Promise<SquareGlobal>>();

function sdkUrl(environment: string | null | undefined): string {
    return environment === 'production'
        ? 'https://web.squarecdn.com/v1/square.js'
        : 'https://sandbox.web.squarecdn.com/v1/square.js';
}

function loadSdk(url: string): Promise<SquareGlobal> {
    const cached = loaders.get(url);
    if (cached) return cached;

    const promise = new Promise<SquareGlobal>((resolve, reject) => {
        if (window.Square) {
            resolve(window.Square);
            return;
        }

        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => {
            if (window.Square) resolve(window.Square);
            else reject(new Error('Square SDK loaded but did not register'));
        };
        script.onerror = () => {
            // Let a later attempt retry instead of caching the failure.
            loaders.delete(url);
            reject(new Error('Could not load the payment form'));
        };
        document.head.appendChild(script);
    });

    loaders.set(url, promise);
    return promise;
}

interface UseSquarePaymentsResult {
    payments: SquarePayments | null;
    ready: boolean;
    error: string | null;
}

export function useSquarePayments(
    applicationId: string | null | undefined,
    locationId: string | null | undefined,
    environment: string | null | undefined
): UseSquarePaymentsResult {
    const [payments, setPayments] = useState<SquarePayments | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!applicationId || !locationId) return;

        let cancelled = false;
        void (async () => {
            try {
                const square = await loadSdk(sdkUrl(environment));
                if (cancelled) return;
                setPayments(square.payments(applicationId, locationId));
            } catch (err) {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : String(err));
                }
            }
        })();

        return () => { cancelled = true; };
    }, [applicationId, locationId, environment]);

    return { payments, ready: payments !== null, error };
}
